<template>
  <div v-clickoutside="onMouseOut">
    <label>{{ label }}</label>
    <ul
      :id="id"
      ref="dropdown"
      :class="[
        'nt-select2', { 'nt-select2--opened': opened }, { 'nt-select2--rounded': rounded }, getTheme
      ]"
    >
      <!-- Button -->
      <li>
        <button
          :id="id + '-arrow'"
          class="nt-select2__label"
          :class="{ 'inactive' : value.Value == null, 'error' : error }"
          tabindex="0"
          @click.prevent="changeVisibility"
        >
          {{ value.Text }}
          <span class="arrow">
            <svg><use xlink:href="#c-icon-accordion-arrow" /></svg>
          </span>
        </button>
        <ul
          ref="content"
          class="nt-select2__content"
          :class="contentClass"
          tabindex="-1"
          @blur="onMouseOut"
        >
          <li
            v-for="(item, i) in data"
            :id="id + '-item-' + i"
            :key="item.id"
            :class="{'active': selected && selected.Value === item.Value }"
            :tabindex="opened ? '0':'-1'"
            @keydown.enter.prevent="onClickDropItem(item)"
            @keydown.up.prevent="focusOnPrevious"
            @keydown.down.prevent="focusOnNext"
            @keydown.esc="onMouseOut"
          >
            <button
              v-if="selected && selected.Value"
              tabindex="-1"
              type="button"
              :class="{ 'inactive' : item.Value == null }"
              @mousedown.prevent="onClickDropItem(item)"
            >
              {{ item.Text }}
            </button>
            <button
              v-else-if="i > 0"
              tabindex="-1"
              type="button"
              :class="{ 'inactive' : item.Value == null }"
              @mousedown.prevent="onClickDropItem(item)"
            >
              {{ item.Text }}
            </button>
          </li>
        </ul>
      </li>
    </ul>
    <div
      v-if="error"
      class="nt-form-error"
    >
      {{ error }}
    </div>
    <div
      v-if="helperText"
      class="nt-form-text-helper"
    >
      {{ helperText }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import emitter from 'tiny-emitter/instance'

export default {
  name: 'nt-select',
  props: {
    id: {
      type: String,
      default: 'select'
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    dataSource: {
      type: Array,
      default: []
    },
    validators: {
      type: Array,
      default: []
    },
    rounded: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'default'
    },
    helperText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      data: [],
      default: {
        Text: this.$props.placeholder,
        Value: null
      },
      opened: false,
      selected: null,
      error: null,
      contentPosition: 'bottom'
    }
  },
  computed: {
    ...mapState('formStore', ['errors']),
    getTheme() {
      return `nt-select2--${this.theme}`
    },
    contentClass() {
      return `nt-select2__content--${this.contentPosition}`
    },
    value() {
      return this.selected ? this.selected : this.default
    }
  },
  watch: {
    errors: {
      handler(values) {
        const { id } = this
        const error = values.find((value) => value[id]) || {}
        this.error = error[id]
      },
      deep: true
    }
  },
  created() {
    this.data.push(this.default)
    this.data = this.data.concat(this.dataSource)
    // Add to form initial values
    this.changeFormData({
      [this.id]: this.selected ? this.selected.Value : null
    })
    // Add validators to form
    this.changeValidators({
      [this.id]: this.validators
    })
  },
  mounted() {
    this.checkForUrlParamPreSelection()
    window.addEventListener('scroll', () => {
      this.showIn()
    })
  },
  methods: {
    ...mapActions('formStore', ['changeFormData', 'changeValidators']),
    getAllUrlParams(url) {
      let queryString = url ? url.split('?')[1] : window.location.search.slice(1)
      const obj = {}
      if (queryString) {
        const [queryStringStart] = queryString.split('#')
        queryString = queryStringStart
        const arr = queryString.split('&')
        for (let i = 0; i < arr.length; i++) {
          const a = arr[i].split('=')
          let paramName = a[0]
          let paramValue = typeof a[1] === 'undefined' ? true : a[1]
          paramName = paramName.toLowerCase()
          if (typeof paramValue === 'string') paramValue = paramValue.toLowerCase()
          if (paramName.match(/\[(\d+)?\]$/)) {
            const key = paramName.replace(/\[(\d+)?\]/, '')
            if (!obj[key]) obj[key] = []
            if (paramName.match(/\[\d+\]$/)) {
              const index = /\[(\d+)\]/.exec(paramName)[1]
              obj[key][index] = paramValue
            } else {
              obj[key].push(paramValue)
            }
          } else if (!obj[paramName]) {
            obj[paramName] = paramValue
          } else if (obj[paramName] && typeof obj[paramName] === 'string') {
            obj[paramName] = [obj[paramName]]
            obj[paramName].push(paramValue)
          } else {
            obj[paramName].push(paramValue)
          }
        }
      }
      return obj
    },
    focusOnPrevious(e) {
      if (e.target.previousElementSibling && e.target.previousElementSibling.tagName === 'LI') {
        e.target.previousElementSibling.focus()
      } else {
        this.onMouseOut()
      }
    },
    focusOnNext(e) {
      if (e.target.nextElementSibling && e.target.nextElementSibling.tagName === 'LI') {
        e.target.nextElementSibling.focus()
      } else {
        this.onMouseOut()
      }
    },
    changeVisibility() {
      this.opened = !this.opened
      this.$nextTick(() => {
        this.showIn()
      })
    },
    onClickDropItem(item) {
      this.selected = item
      this.opened = false
      this.changeForm()

      emitter.emit('selectedItem', { text: item.Text, id: this.id })
    },
    onMouseOut() {
      this.opened = false
    },
    changeForm() {
      emitter.emit('selectFormChanged', { id: this.id, label: this.label })
      emitter.emit('changeForm', {
        [this.id]: this.selected ? this.selected.Value : null
      })
    },
    showIn() {
      if (!this.opened) return

      const { dropdown, content } = this.$refs

      const selectBounding = dropdown.getBoundingClientRect()
      const contentLimit = Math.abs(content.clientHeight / 2)

      const { top } = selectBounding
      const bottom = window.innerHeight - selectBounding.bottom

      if (top >= contentLimit && bottom >= contentLimit) {
        content.style.top = `-${contentLimit - (selectBounding.height / 2)}px`
        this.contentPosition = 'center'
      } else if (top >= contentLimit) {
        content.style.top = `-${content.clientHeight}px`
        this.contentPosition = 'top'
      } else {
        content.style.top = null
        this.contentPosition = 'bottom'
      }
    },
    checkForUrlParamPreSelection() {
      if (this.getAllUrlParams()[this.id]) {
        this.data.forEach(item => {
          if (item.Text.toLowerCase() === decodeURIComponent(this.getAllUrlParams()[this.id]).toLowerCase()) {
            this.onClickDropItem(item)
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .nt-select2 {
    $wrap: &;
    margin: 0;
    position: relative;
    background: $white;
    list-style: none;
    padding-inline-start: 0;
    padding: 0;

    &__label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border: 0.125rem solid $neutral-04;
      color: $blue-04;
    }

    &--rounded {
      background: $white;
      border-radius: 2.1875rem;
      border: none;

      .nt-select2__label {
        border: none;
      }
    }

    & .inactive {
      font-weight: 400;
      color: $neutral-01;
      font-family: Montserrat, sans-serif;
    }

    & .active {
      background: #fafafa;
    }

    & .arrow {
      display: flex;
      fill: $neutral-01;
      color: $neutral-01;
      height: 2rem;
      width: 2rem;
    }

    button {
      width: 100%;
      height: 100%;
      text-align: left;
      font-size: 1rem;
      margin: 0;
      display: flex;
      padding: 1.25rem;
      border-radius: 0.125rem;
      font-family: Montserrat, sans-serif;
      color: $neutral-02;

      &:hover {
        cursor: pointer;
      }

      &.error {
        border-color: $accent-orange;
      }
    }

    &__content {
      position: absolute;
      width: 100%;
      display: none;
      z-index: 50;
      border-style: solid;
      border-width: 0.125rem;
      border-color: $neutral-02;
      list-style: none;
      padding-inline-start: 0;
      padding: 0;
    }

    &--opened {
      #{$wrap}__content {
        display: block;
        overflow: auto;
        max-height: 18.125rem;
        background-color: $white;

        &--bottom {
          border-width: 0 0.0625rem 0.0625rem;
        }

        & > li {
          background-color: #fafafa;
          &:not(:last-child):not(:first-child) {
            border-bottom: 0.0625rem solid $blue-06;
          }
          &:hover {
            background-color: $white;
          }
        }
      }
    }
    &--small {
      button {
        padding: 0.625rem;
        font-size: 1rem;
      }
    }
  }
  .contrast-mode {
    .nt-select2 {
      &__label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border: 0.125rem solid $neutral-04;
        color: $white;
      }

      .nt-select2__content {
        li {
          button {
            background-color: #fafafa;
            color: $white !important;
            &:not(:last-child):not(:first-child) {
              border-bottom: 0.0625rem solid $blue-06;
            }
            &:hover {
              color: $black !important;
            }
          }
        }
      }
    }
  }
</style>
